import { Highlight } from "react-instantsearch";
import { HIGHLIGHT_PRE_TAG, renderHighlighted } from "./searchHighlight";

// Priority order for "why did this row match?". `title` is deliberately absent
// -- it is already rendered highlighted on the first line.
const INCIDENT_SNIPPET_FIELDS = [
  ["meta_claim_number", "Claim number"],
  ["driver_name", "Driver"],
  ["driver_email", "Driver email"],
  ["driver_phone", "Driver phone"],
  ["car_vin", "VIN"],
  ["car_license_plate", "Plate"],
  ["dmv_plate", "DMV plate"],
  ["meta_crm_name", "CRM name"],
  ["meta_crm_id", "CRM id"],
  ["meta_crm_notes", "CRM note"],
  ["meta_external_ids", "External id"],
  ["meta_monday_row_title", "Monday row"],
  ["meta_monday_row_id", "Monday row id"],
  ["meta_rideshare_incident_id", "Rideshare id"],
  ["meta_conversation_id", "Conversation id"],
  ["meta_archive_reason", "Archive reason"],
  ["meta_category", "Category"],
  ["meta_sub_category", "Sub-category"],
  ["meta_additional_info", "Additional info"],
  ["location", "Location"],
  ["description", "Description"],
  ["notes", "Notes"],
];

function findMatchedField(highlightResult) {
  if (!highlightResult) return null;

  for (const [attribute, label] of INCIDENT_SNIPPET_FIELDS) {
    const entry = highlightResult[attribute];
    if (!entry) continue;

    // List-valued attributes (the meta_ fields -- meta_crm_notes is a list and
    // meta_external_ids is a dict flattened to one) arrive as arrays of
    // {value}. Show the element that matched, not the whole list.
    const candidates = Array.isArray(entry) ? entry : [entry];
    const matched = candidates.find(
      (candidate) =>
        typeof candidate?.value === "string" &&
        candidate.value.includes(HIGHLIGHT_PRE_TAG)
    );

    if (matched) return { label, value: matched.value };
  }

  return null;
}

function MatchedSnippet({ hit }) {
  const match = findMatchedField(hit._highlightResult);
  if (!match) return null;

  return (
    <p className="search-hit-secondary">
      <span className="search-hit-snippet-label">{match.label}:</span>{" "}
      {renderHighlighted(match.value)}
    </p>
  );
}

// incident_date is a date-only ISO string. Passing it to `new Date` parses it
// as UTC midnight, which renders as the previous day in New York.
function formatIncidentDate(value) {
  if (typeof value !== "string") return null;

  const [year, month, day] = value.split("T")[0].split("-");
  if (!year || !month || !day) return null;

  return `${month}/${day}/${year}`;
}

function DriverHit({ hit }) {
  return (
    <a href={hit.frontend_url} className="hit hit-driver search-hit-content">
      <div className="search-hit-row">
        <span className="search-hit-icon">
          <i className="fa fa-user" />
        </span>

        <span className="search-hit-title search-hit-ellipsis">
          <Highlight attribute="name" hit={hit} />
        </span>

        <span className="search-hit-meta search-hit-ellipsis">
          {hit.email || hit.phone}
        </span>

        {hit.branch_name && (
          <span className="search-hit-meta">
            · {hit.branch_name}
          </span>
        )}
      </div>
    </a>
  );
}

function CarHit({ hit }) {
  return (
    <a href={hit.frontend_url} className="hit hit-car search-hit-content">
      <div className="search-hit-row">
        <span className="search-hit-icon">
          <i className="fa fa-car" />
        </span>

        <span className="search-hit-title search-hit-ellipsis">
          {hit.year} {hit.make} {hit.model}
        </span>

        <span className="search-hit-meta search-hit-ellipsis">
          <Highlight attribute="license_plate" hit={hit} />
        </span>

        {hit.branch_name && (
          <span className="search-hit-meta">
            · {hit.branch_name}
          </span>
        )}
      </div>
    </a>
  );
}

function ConversationHit({ hit }) {
  return (
    <a href={hit.frontend_url} className="hit hit-convo search-hit-content">
      <div className="search-hit-row">
        <span className="search-hit-icon">
          <i className="fa fa-comments-o" />
        </span>

        <span className="search-hit-title search-hit-ellipsis">
          {hit.subject}
        </span>

        <span className="search-hit-meta search-hit-ellipsis">
          {hit.channel_name}
        </span>

        {hit.date_added && (
          <span className="search-hit-meta">
            · {new Date(hit.date_added).toLocaleDateString()}
          </span>
        )}
      </div>

      {hit.message_subject_sliding_window && (
        <p className="search-hit-secondary">
          <Highlight
            attribute="message_subject_sliding_window"
            hit={hit}
          />
        </p>
      )}
    </a>
  );
}

function TaskHit({ hit }) {
  return (
    <a href={hit.frontend_url} className="hit hit-task search-hit-content">
      <div className="search-hit-row">
        <span className="search-hit-icon">
          <i className="fa fa-tasks" />
        </span>

        <span className="search-hit-title search-hit-ellipsis">
          <Highlight attribute="title" hit={hit} />
        </span>

        {hit.status && (
          <span className="search-hit-meta">
            · {hit.status}
          </span>
        )}
        {hit.date_added && (
          <span className="search-hit-meta">
            · {new Date(hit.date_added).toLocaleDateString()}
          </span>
        )}

        {hit.board_title && (
          <span className="search-hit-meta search-hit-ellipsis">
            · {hit.board_title}
          </span>
        )}
      </div>
    </a>
  );
}

function IncidentHit({ hit }) {
  const incidentDate = formatIncidentDate(hit.incident_date);

  return (
    <a href={hit.frontend_url} className="hit hit-incident search-hit-content">
      <div className="search-hit-row">
        <span className="search-hit-icon">
          <i className="fa fa-exclamation-triangle" />
        </span>

        <span className="search-hit-title search-hit-ellipsis">
          <Highlight attribute="title" hit={hit} />
        </span>

        {hit.car_name && (
          <span className="search-hit-meta search-hit-ellipsis">
            · {hit.car_name}
          </span>
        )}

        {(hit.status_display || hit.status) && (
          <span className="search-hit-meta">
            · {hit.status_display || hit.status}
          </span>
        )}

        {incidentDate && (
          <span className="search-hit-meta">
            · {incidentDate}
          </span>
        )}

        {hit.branch_name && (
          <span className="search-hit-meta search-hit-ellipsis">
            · {hit.branch_name}
          </span>
        )}

        {hit.is_archived && (
          <span className="search-hit-badge search-hit-badge-archived">
            Archived
          </span>
        )}
      </div>

      <MatchedSnippet hit={hit} />
    </a>
  );
}

const RECORD_TYPE_LABELS = {
  party: "Party",
  person: "Person",
  vehicle: "Vehicle",
  liability: "Liability claim",
  subrogation: "Subrogation claim",
};

// The row already says what it is, so the secondary detail is whatever
// identifies this particular row beyond its label.
function partyDetail(hit) {
  if (hit.record_type === "person") {
    return [hit.phone_number, hit.email, hit.driver_license].filter(Boolean).join(" · ");
  }
  if (hit.record_type === "vehicle") {
    return [hit.vin, hit.plate_number, hit.policy_number].filter(Boolean).join(" · ");
  }
  return hit.party_type || "";
}

// Both claim types carry the same pair of identifying fields.
function claimDetail(hit) {
  return [hit.tpa_name, hit.status_display].filter(Boolean).join(" · ");
}

function IncidentRelatedHit({ hit, icon, detail }) {
  return (
    <a href={hit.frontend_url} className="hit hit-incident-related search-hit-content">
      <div className="search-hit-row">
        <span className="search-hit-icon">
          <i className={icon} />
        </span>

        <span className="search-hit-badge search-hit-badge-record-type">
          {RECORD_TYPE_LABELS[hit.record_type] || hit.record_type}
        </span>

        <span className="search-hit-title search-hit-ellipsis">
          <Highlight attribute="label" hit={hit} />
        </span>

        {detail && (
          <span className="search-hit-meta search-hit-ellipsis">· {detail}</span>
        )}

        {hit.branch_name && (
          <span className="search-hit-meta search-hit-ellipsis">· {hit.branch_name}</span>
        )}
      </div>

      {hit.incident_title && (
        <p className="search-hit-secondary">
          <span className="search-hit-snippet-label">Incident:</span>{" "}
          {/* incident_title is searchable, so a query that matched only it
              would otherwise leave the row with no visible reason to be here. */}
          <Highlight attribute="incident_title" hit={hit} />
        </p>
      )}
    </a>
  );
}

function IncidentPartyHit({ hit }) {
  return (
    <IncidentRelatedHit hit={hit} icon="fa fa-users" detail={partyDetail(hit)} />
  );
}

function LiabilityClaimHit({ hit }) {
  return (
    <IncidentRelatedHit
      hit={hit}
      icon="fa fa-gavel"
      detail={claimDetail(hit)}
    />
  );
}

function SubrogationClaimHit({ hit }) {
  return (
    <IncidentRelatedHit
      hit={hit}
      icon="fa fa-refresh"
      detail={claimDetail(hit)}
    />
  );
}

export {
  DriverHit,
  CarHit,
  ConversationHit,
  TaskHit,
  IncidentHit,
  IncidentPartyHit,
  LiabilityClaimHit,
  SubrogationClaimHit,
};